import React, { useRef, useEffect, useState } from "react";
import { FaCheck } from "react-icons/fa";

const palette = {
  text: "#18171b",
  subtitle: "#4b70f5",
  divider: "#e5e8f0",
  check: "#6666ff",
};

const plans = [
  { name: "Unit"},
  { name: "Mesh" },
  { name: "Enterprise"},
];

const features = [
  {
    label: "Data Storage",
    desc: "Data is stored using the medallion architecture, preparing it from raw to aggregated products for actionable insights.",
    values: [true, true, true],
  },
  {
    label: "Data Flows",
    desc: "Preconfigured and secured flows process data quickly and reliably, turning raw source data into final data products.",
    values: [true, true, true],
  },
  {
    label: "Data Insights",
    desc: "Dashboards and reports provide the insights you need to make data-driven decisions across your company.",
    values: [true, true, true],
  },
  {
    label: "Data Agentic Mesh",
    desc: "Automated data management, insights, and actions on your data using AI reasoning. Let Agentic AI handle the heavy lifting.",
    values: [false, true, true],
  },
  {
    label: "Data Monitoring",
    desc: "AI-powered monitoring ensures the platform and data run smoothly and are fault-tolerant, taking proactive measures when needed.",
    values: [false, true, true],
  },
  {
    label: "Data Infra Security",
    desc: "A secure-by-design setup ensures data is only processed and used where allowed —no unknown access to your valuable data.",
    values: [false, false, true],
  },
];

const PricingComparisonTable = () => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  // Intersection Observer for scroll-in animation
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 700);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Animation helpers
  const getFadeInStyle = (delay = 0) => ({
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(32px)',
    transition: `opacity 0.7s cubic-bezier(.4,0,.2,1) ${delay}s, transform 0.7s cubic-bezier(.4,0,.2,1) ${delay}s`,
  });

  // Grid columns: 1 for feature, 3 for plans
  const gridTemplate = isMobile
    ? '2.5fr 1fr 1fr 1fr' // More space for left col, less for checkmarks
    : 'minmax(220px,2.2fr) repeat(3, minmax(120px,1fr))';

  return (
    <section
      ref={sectionRef}
      style={{
        ...styles.section,
        borderRadius: isMobile ? 10 : styles.section.borderRadius,
        margin: isMobile ? '24px auto 0 auto' : styles.section.margin,
        padding: isMobile ? '18px 0 8px 0' : styles.section.padding,
        maxWidth: isMobile ? 370 : styles.section.maxWidth,
      }}
    >
      {/* Header row: plan names */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: gridTemplate,
          alignItems: 'end',
          gap: isMobile ? 6 : 0,
          marginBottom: isMobile ? 2 : 8,
          ...getFadeInStyle(0),
        }}
      >
        <div style={{ minHeight: 24 }} />
        {plans.map((plan, i) => (
          <div key={plan.name} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: isMobile ? 13 : 18, fontWeight: 600, color: palette.text, marginBottom: 2, fontFamily: 'Inter, sans-serif' }}>{plan.name}</div>
          </div>
        ))}
      </div>
      <div style={styles.divider} />
      {/* Features rows */}
      <div>
        {features.map((feature, idx) => (
          <div
            key={feature.label}
            style={{
              display: 'grid',
              gridTemplateColumns: gridTemplate,
              alignItems: 'start',
              padding: isMobile ? '18px 0 8px 0' : '32px 0 18px 0',
              borderBottom: `1px solid ${palette.divider}`,
              gap: isMobile ? 6 : 0,
              ...getFadeInStyle(0.12 + idx * 0.08),
            }}
          >
            <div style={{ paddingRight: isMobile ? 0 : 16 }}>
              <div style={{ fontSize: isMobile ? 17 : 19, fontWeight: 700, color: palette.text, marginBottom: 4, fontFamily: 'Inter, sans-serif' }}>{feature.label}</div>
              <div style={{ fontSize: isMobile ? 13 : 15, color: '#222', fontWeight: 400, fontFamily: 'Inter, sans-serif', lineHeight: 1.5 }}>{feature.desc}</div>
            </div>
            {feature.values.map((val, i) => (
              <div key={i} style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 32 }}>
                {val ? (
                  <FaCheck size={isMobile ? 16 : 22} color={palette.check} />
                ) : (
                  <span style={{ fontSize: isMobile ? 18 : 28, color: '#bbb', fontWeight: 400, fontFamily: 'Inter, sans-serif', lineHeight: 1 }}>-</span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
};

const styles = {
  section: {
    borderRadius: 18,
    margin: '48px auto 0 auto',
    padding: '36px 0 24px 0',
    maxWidth: 1100,
    width: '95%',
    fontFamily: 'Inter, sans-serif',
    marginBottom: "100px",
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 24,
    marginBottom: 8,
    padding: '0 0 0 0',
  },
  headerTitleWrap: {
    flex: 2.2,
    minWidth: 220,
  },
  title: {
    fontSize: 32,
    fontWeight: 700,
    color: palette.text,
    marginBottom: 2,
    fontFamily: 'Inter, sans-serif',
  },
  subtitle: {
    fontSize: 17,
    color: '#222',
    fontWeight: 400,
    marginBottom: 0,
    fontFamily: 'Inter, sans-serif',
  },
  planHeaderWrap: {
    flex: 3,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 0,
  },
  planHeaderCol: {
    flex: 1,
    minWidth: 120,
    textAlign: 'center',
  },
  planName: {
    fontSize: 18,
    fontWeight: 600,
    color: palette.text,
    marginBottom: 2,
    fontFamily: 'Inter, sans-serif',
  },
  planPrice: {
    fontSize: 18,
    fontWeight: 400,
    color: '#222',
    fontFamily: 'Inter, sans-serif',
  },
  divider: {
    borderBottom: `2px solid ${palette.divider}`,
    margin: '18px 0 0 0',
    width: '100%',
  },
  featureRow: {
    display: 'flex',
    fontWeight: 300,
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: '32px 0 18px 0',
    borderBottom: `1px solid ${palette.divider}`,
    gap: 0,
  },
  featureDescWrap: {
    // Remove flex: 2.2 so columns align
    minWidth: 220,
    paddingRight: 16,
  },
  featureLabel: {
    fontSize: 19,
    fontWeight: 700,
    color: palette.text,
    marginBottom: 4,
    fontFamily: 'Inter, sans-serif',
  },
  featureDesc: {
    fontSize: 15,
    color: '#222',
    fontWeight: 400,
    fontFamily: 'Inter, sans-serif',
    lineHeight: 1.5,
  },
  checkCol: {
    flex: 1,
    minWidth: 120,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Inter, sans-serif',
  },
  dash: {
    fontSize: 28,
    color: '#bbb',
    fontWeight: 400,
    fontFamily: 'Inter, sans-serif',
    lineHeight: 1,
  },
};

export default PricingComparisonTable;
